import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../../../contexts/ThemeContext';
import { motion, AnimatePresence } from 'framer-motion';
import { FaCheck, FaArrowRight, FaRobot, FaStore, FaCogs } from 'react-icons/fa';
import publicData from '../../../data/publicData.json';

const OfferPopup = () => {
  const { theme, toggleTheme } = useTheme();
  const [isVisible, setIsVisible] = useState(false);
  const [currentOffer, setCurrentOffer] = useState(0);
  const [imageError, setImageError] = useState({});

  const { offers } = publicData;
  const { items: offersList, settings } = offers;

  // Map icon strings to actual components
  const iconMap = {
    FaRobot: FaRobot,
    FaStore: FaStore,
    FaCogs: FaCogs
  };

  useEffect(() => {
    // Check if popup was shown in the last 24 hours
    const lastShown = localStorage.getItem(settings.storageKey);
    const now = new Date().getTime();
    
    if (!lastShown || (now - parseInt(lastShown)) > settings.storageDuration) {
      // Show popup after configured delay
      const timer = setTimeout(() => {
        setIsVisible(true);
        localStorage.setItem(settings.storageKey, now.toString());
      }, settings.showAfter);

      return () => clearTimeout(timer);
    }
  }, [settings]);

  useEffect(() => {
    if (isVisible) {
      // Rotate offers every configured interval
      const interval = setInterval(() => {
        setCurrentOffer((prev) => (prev + 1) % offersList.length);
      }, settings.rotateInterval);

      return () => clearInterval(interval);
    }
  }, [isVisible, offersList.length, settings.rotateInterval]);

  const handleClose = () => {
    setIsVisible(false);
  };

  const handleImageError = (index) => {
    setImageError(prev => ({ ...prev, [index]: true }));
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center"
        >
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.7 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black"
            onClick={handleClose}
          />

          {/* Popup */}
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className={`relative w-full max-w-4xl mx-4 rounded-xl shadow-2xl overflow-hidden ${
              theme === 'dark' ? 'bg-gray-800' : 'bg-white'
            }`}
          >
            <button
              onClick={handleClose}
              className={`absolute top-4 right-4 ${
                theme === 'dark' ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              ✕
            </button>

            <AnimatePresence mode="wait">
              <motion.div
                key={currentOffer}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="flex flex-col md:flex-row"
              >
                {/* Image Section */}
                <div className="w-full md:w-1/2 h-64 md:h-auto">
                  <div className={`w-full h-full ${offersList[currentOffer].bgColor[theme]} flex items-center justify-center`}>
                    {imageError[currentOffer] ? (
                      <div className="flex flex-col items-center justify-center p-8">
                        {React.createElement(iconMap[offersList[currentOffer].icon], { className: "w-24 h-24" })}
                        <h3 className={`mt-4 text-2xl font-bold ${offersList[currentOffer].color[theme]}`}>
                          {offersList[currentOffer].title}
                        </h3>
                      </div>
                    ) : (
                      <img
                        src={offersList[currentOffer].image}
                        alt={offersList[currentOffer].title}
                        className="w-full h-full object-cover"
                        onError={() => handleImageError(currentOffer)}
                      />
                    )}
                  </div>
                </div>

                {/* Content Section */}
                <div className="w-full md:w-1/2 p-8">
                  {!imageError[currentOffer] && (
                    <h3 className={`text-3xl font-bold mb-2 ${offersList[currentOffer].color[theme]}`}>
                      {offersList[currentOffer].title}
                    </h3>
                  )}
                  <p className={`text-xl mb-6 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                    {offersList[currentOffer].description}
                  </p>
                  
                  {/* Features List */}
                  <div className="mb-6">
                    {offersList[currentOffer].features.map((feature, index) => (
                      <div key={index} className="flex items-start mb-3">
                        <FaCheck className={`mt-1 mr-3 ${offersList[currentOffer].color[theme]}`} />
                        <span className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>
                          {feature}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* Price */}
                  <div className={`text-2xl font-bold mb-6 ${offersList[currentOffer].color[theme]}`}>
                    {offersList[currentOffer].price}
                  </div>

                  {/* CTA Button */}
                  <Link
                    to={offersList[currentOffer].link}
                    className={`inline-flex items-center px-8 py-4 rounded-lg font-medium text-lg ${
                      theme === 'dark'
                        ? 'bg-blue-600 hover:bg-blue-700 text-white'
                        : 'bg-blue-500 hover:bg-blue-600 text-white'
                    }`}
                  >
                    Get Started
                    <FaArrowRight className="ml-2" />
                  </Link>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Offer indicators */}
            <div className="absolute bottom-4 left-0 right-0 flex justify-center space-x-2">
              {offersList.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentOffer(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    index === currentOffer
                      ? theme === 'dark'
                        ? 'bg-blue-400 scale-125'
                        : 'bg-blue-600 scale-125'
                      : theme === 'dark'
                      ? 'bg-gray-600 hover:bg-gray-500'
                      : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                />
              ))}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default OfferPopup; 